import React from 'react';
import type { ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

import { Transition } from '@headlessui/react';

import { TbX } from 'react-icons/tb';

import Icon from 'components-new/icon/icon';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;

  backdrop?: boolean;
  direction?: 'left' | 'right' | 'top' | 'bottom';
  containerClassName?: string;
  drawerClassName?: string;
  contentClassName?: string;
  transitionEnter?: string;
  transitionLeave?: string;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  backdrop = true,
  direction = 'left',
  drawerClassName,
  contentClassName,
  transitionEnter,
  transitionLeave,
  containerClassName,
}) => {
  let fromTransform = '';
  let flexAlignment = '';
  let rounded = '';
  let defaultDimensions = '';

  switch (direction) {
    case 'left':
      fromTransform = '-translate-x-full';
      flexAlignment = 'justify-start';
      rounded = 'rounded-r';
      defaultDimensions = 'h-full w-[100vw] sm:w-64';
      break;
    case 'right':
      fromTransform = 'translate-x-full';
      flexAlignment = 'justify-end';
      rounded = 'rounded-l';
      defaultDimensions = 'h-full w-[100vw] sm:w-64';
      break;
    case 'top':
      fromTransform = '-translate-y-full';
      flexAlignment = 'justify-center items-start';
      rounded = 'rounded-b';
      defaultDimensions = 'w-[100vw] max-h-[300px]';
      break;
    case 'bottom':
      fromTransform = 'translate-y-full';
      flexAlignment = 'justify-center items-end';
      rounded = 'rounded-t';
      defaultDimensions = 'w-[100vw] max-h-[300px]';
      break;

    default:
      fromTransform = '-translate-x-full';
      flexAlignment = 'justify-center items-center';
  }

  const containerClassNames = twMerge(
    'fixed z-10 inset-0 flex',
    flexAlignment,
    containerClassName,
  );

  const drawerClassNames = twMerge(
    'relative z-20 bg-white px-2 pb-2 overflow-y-auto',
    defaultDimensions,
    rounded,
    drawerClassName,
  );

  const contentClassNames = twMerge('pt-[30px]', contentClassName);

  return (
    <Transition show={isOpen}>
      <div className={containerClassNames}>
        {backdrop && (
          <Transition.Child
            enter="transition-opacity duration-300 ease-in-out"
            enterFrom="opacity-0"
            enterTo="opacity-50"
            leave="transition-opacity duration-300 ease-in-out"
            leaveFrom="opacity-50"
            leaveTo="opacity-0"
          >
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className="fixed inset-0 bg-black opacity-50 transition-opacity h-[100vh] w-[100vw]"
              onClick={onClose}
            />
          </Transition.Child>
        )}
        <Transition.Child
          enter={
            transitionEnter || 'transform transition ease-in-out duration-300'
          }
          enterFrom={fromTransform}
          enterTo="translate-x-0 translate-y-0"
          leave={
            transitionLeave || 'transform transition ease-in-out duration-300'
          }
          leaveFrom="translate-x-0 translate-y-0"
          leaveTo={fromTransform}
        >
          <div className={drawerClassNames}>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-2 right-2"
            >
              <Icon icon={TbX} size={20} />
            </button>
            <div className={contentClassNames}>{children}</div>
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
};

export default Drawer;
